/*
* This page is used in two situations:
* 1. Upon new user activation: User must set a new password and new pair of security question and answer.
* 2. First time logging in using a temp password set by admin, after user forgot the password.
*/

import React , { useEffect, useState } from 'react';
import SplashScreen from '../components/SplashScreen';
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, Container, Dropdown, DropdownButton, Form, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { BiHelpCircle, BiHide, BiShow } from "react-icons/bi";
import { axiosNoAuth_IDENT_MGMT_API } from '../utils/AxoisUtilComponent';
import { LoadingSpinnerForModal } from '../utils/uiUtils';

import { useTranslation } from 'react-i18next';
import { translateAPIError } from '../utils/apiUtils';


const NewPwdSecQnA = (props) => {

  const { i18n, t } = useTranslation(["common"]);

  const navigate = useNavigate();

  const { username } = useParams();

  const [errorMessage, setErrorMessage] = useState('');

  const [loading, setLoading] = useState(false);

  const [hideTempPwd,    setHideTempPwd]    = useState(true);
  const [hideNewPwd,     setHideNewPwd]     = useState(true);
  const [hideConfirmPwd, setHideConfirmPwd] = useState(true);

  const [secQuestionList, setSecQuestionList] = useState([]);
  const [pwdComplexity,   setPwdComplexity]   = useState({});


  // Initialize state
  useEffect(() => {
    fetchPublicConfig();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  function fetchPublicConfig() {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosNoAuth_IDENT_MGMT_API.get("public-config");

        resolve(res.data);

        if (res.status === 200) {
          if (res.data[0] && res.data[0].secQuestions) {
            setSecQuestionList(res.data[0].secQuestions);
          } else {
            setErrorMessage(t("I18N_ERR_fetch_seq_q_list"));
          }

          if (res.data[0] && res.data[0].pwdComplexity) {
            // Decode password complxity info
            let decodedInfo = {}

            decodedInfo.testRegExp = decodeURIComponent(res.data[0].pwdComplexity.testRegExp);

            decodedInfo.hint = {};
            decodedInfo.hint.en    = decodeURIComponent(res.data[0].pwdComplexity.hint.en);
            decodedInfo.hint.zh_HK = decodeURIComponent(res.data[0].pwdComplexity.hint.zh_HK);
            decodedInfo.hint.zh_CN = decodeURIComponent(res.data[0].pwdComplexity.hint.zh_CN);

            setPwdComplexity(decodedInfo);
          } else {
            setErrorMessage(t("I18N_ERR_fetch_pwd_hint"));
          }
        }
        setErrorMessage('');
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrorMessage(t(translateAPIError(error)));
        }
      }
    });
  }


  // Update password
  const updatePwdSecQnA = () => {

    const reqBody = {
      username:     username     ? username.trim()     : username,
      newPassword:  password     ? password.trim()     : password,
      tempPassword: tempPassword ? tempPassword.trim() : tempPassword,
      secQuestion:  secQuestionList.find( ({ code }) => code === secQuestion ).displayName[i18n.language],
      secAnswer:    secAnswer    ? secAnswer.trim()    : secAnswer
    }

    return new Promise(async (resolve, reject) => {
      setLoading(true);

      try {
        const res = await axiosNoAuth_IDENT_MGMT_API.post("set-pwd-sec", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setLoading(false);
          navigate("/AdminHome/Login/" + i18n.language);
        }
      } catch (error) {
        setLoading(false);
        setErrorMessage(t(translateAPIError(error)));
        //reject(error);
      }
    });
  }

  const [newPwdSecQnA, setNewPwdSecQnA] = useState(
    {
      password: '',
      confirmPassword: '',
      tempPassword: '',
      secQuestion: '',
      secAnswer: ''
    }
  );

  let { password, confirmPassword, tempPassword, secQuestion, secAnswer } = newPwdSecQnA;

  const onPasswordInputChange = (e) => {
    setNewPwdSecQnA({ ...newPwdSecQnA, [e.target.name]: e.target.value.trim() })
  }

  const onSecQuestionSelect = (key, event) => {
    setNewPwdSecQnA({ ...newPwdSecQnA, secQuestion: key })
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password || password.trim().length === 0) {
      setErrorMessage(t("I18N_ERR_Need_Password"));
    } else if (!validatePwdComplexity(password)) {
      setErrorMessage(t("I18N_ERR_Poor_Password"));
    } else if (confirmPassword !== password) {
      setErrorMessage(t("I18N_ERR_Pass_Mismatch"));
    } else if (!tempPassword || tempPassword.length === 0) {
      setErrorMessage(t("I18N_ERR_Need_Curr_Pwd"));
    } else if (password === tempPassword) {
      setErrorMessage(t("I18N_ERR_Old_new_pwd_same"));
    } else if (!secQuestion || secQuestion.length === 0) {
      setErrorMessage(t("I18N_ERR_Need_Sec_Q"));
    } else if (!secAnswer || secAnswer.length === 0) {
      setErrorMessage(t("I18N_ERR_Need_Sec_A"));
    } else {
      setErrorMessage('');
      updatePwdSecQnA();
    }
  }

  function validatePwdComplexity(password) {
    // Password policy: Min length, upper case, lower case, special char, etc.
    //let strongPassword = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{12,30}$');
    let strongPassword = new RegExp(pwdComplexity.testRegExp);

    if (!strongPassword.test(password)) {
      return (false);
    } else {
      return (true);
    }
  }

  const handleClose = () => {
    navigate("/AdminHome/Login");
  }

  const handleToggleTempPwd = (e) => {
    e.preventDefault();
    setHideTempPwd(!hideTempPwd);
  }

  const handleToggleNewPwd = (e) => {
    e.preventDefault();
    setHideNewPwd(!hideNewPwd);
  }

  const handleToggleConfirmPwd = (e) => {
    e.preventDefault();
    setHideConfirmPwd(!hideConfirmPwd);
  }


  return (
    <React.Fragment>

      <div>
        <SplashScreen />
      </div>

      <Modal show={true} onHide={handleClose} centered backdrop="static" >
        <fieldset disabled={loading}>
          <Modal.Header closeButton>
            <Modal.Title style={{fontSize:'20px', fontWeight:'200'}}>
              {t("I18N_Reset_pwd_sec_q")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Container>

              <Form onSubmit={handleSubmit} className="m-3" style={{ fontSize: '13px', fontWeight: '300' }}>
                <Form.Group as={Row} className="mb-0 mt-2" controlId="formPlaintextUsername">
                  <Form.Label column sm="4">
                    {t("I18N_Username")}:
                  </Form.Label>
                  <Form.Label column sm="8">
                    <i>{username}</i>
                  </Form.Label>
                </Form.Group>

                <hr />

                <Form.Group as={Row} className="mb-2 mt-2" controlId="formTempPassword">
                  <Form.Label column sm="4">
                    {t("I18N_Temp_pwd")}
                  </Form.Label>
                  <Col sm="8" style={{ position:'relative'}}>
                    { hideTempPwd ?
                      <Form.Control size="sm" type="password" name="tempPassword" value={tempPassword} onChange={(e) => onPasswordInputChange(e)} placeholder="" style={{ fontWeight: '200' }} />
                    :
                      <Form.Control size="sm" type="text"     name="tempPassword" value={tempPassword} onChange={(e) => onPasswordInputChange(e)} placeholder="" style={{ fontWeight: '200' }} />
                    }
                    <span onClick={handleToggleTempPwd} style={{ marginLeft:'-60px', cursor:'pointer', color:'rgb(100,100,100)', position:'absolute', top:'0', right:'20px'}}>
                      { hideTempPwd ?
                        <BiHide size={18} style={{marginTop:'7px'}} />
                      :
                        <BiShow size={18} style={{marginTop:'7px'}} />
                      }
                    </span>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2 mt-2" controlId="formPassword">
                  <Form.Label column sm="4">
                    {t("I18N_New_pwd")}
                  </Form.Label>
                  <Col sm="8" style={{ position:'relative'}}>
                    { hideNewPwd ?
                      <Form.Control size="sm" type="password" name="password" value={password} onChange={(e) => onPasswordInputChange(e)} placeholder="" style={{ fontWeight: '200' }} />
                    :
                      <Form.Control size="sm" type="text"     name="password" value={password} onChange={(e) => onPasswordInputChange(e)} placeholder="" style={{ fontWeight: '200' }} />
                    }
                    <span onClick={handleToggleNewPwd} style={{ marginLeft:'-60px', cursor:'pointer', color:'rgb(100,100,100)', position:'absolute', top:'0', right:'20px'}}>
                      { hideNewPwd ?
                        <BiHide size={18} style={{marginTop:'7px'}} />
                      :
                        <BiShow size={18} style={{marginTop:'7px'}} />
                      }
                    </span>
                    <OverlayTrigger overlay={<Tooltip id="tooltip-pwd-complexity" style={{ fontSize: '11px' }}>
                                               {pwdComplexity.hint ? pwdComplexity.hint[i18n.language] : ''}</Tooltip>}>
                      <span style={{ marginTop: '5px',  position:'absolute', top:'0', right:'-10px', cursor: 'pointer' }}>
                        <BiHelpCircle size={18} />
                      </span>
                    </OverlayTrigger>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2 mt-2" controlId="formConfirmPassword">
                  <Form.Label column sm="4">
                    {t("I18N_Confirm_password")}
                  </Form.Label>
                  <Col sm="8" style={{ position:'relative'}}>
                    { hideConfirmPwd ?
                      <Form.Control size="sm" type="password" name="confirmPassword" value={confirmPassword} onChange={(e) => onPasswordInputChange(e)} placeholder="" style={{ fontWeight: '200' }} />
                    :
                      <Form.Control size="sm" type="text"     name="confirmPassword" value={confirmPassword} onChange={(e) => onPasswordInputChange(e)} placeholder="" style={{ fontWeight: '200' }} />
                    }
                    <span onClick={handleToggleConfirmPwd} style={{ marginLeft:'-60px', cursor:'pointer', color:'rgb(100,100,100)', position:'absolute', top:'0', right:'20px'}}>
                      { hideConfirmPwd ?
                        <BiHide size={18} style={{marginTop:'7px'}} />
                      :
                        <BiShow size={18} style={{marginTop:'7px'}} />
                      }
                    </span>
                  </Col>
                </Form.Group>

                <hr />

                <Form.Group as={Row} className="mb-2 mt-2" controlId="formSecQ">
                  <Form.Label column sm="4">
                    {t("I18N_Sec_question")}
                  </Form.Label>
                  <Col sm="8">
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={((!secQuestion) || (secQuestion.length === 0)) ? t("common:I18N_DROPDOWN_PROMPT") :
                             secQuestionList.find( ({ code }) => code === secQuestion ).displayName[i18n.language] }
                      style={{marginTop: '5px' }}
                      onSelect={onSecQuestionSelect}>
                      {secQuestionList.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.code}  style={{minWidth: '100%',fontSize:'13px'}}>
                            {item.displayName[i18n.language]}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2 mt-2" controlId="formPlaintextPassword">
                  <Form.Label column sm="4">
                    {t("I18N_Sec_answer")}
                  </Form.Label>
                  <Col sm="8">
                    <Form.Control size="sm" type="text" name="secAnswer" value={secAnswer}
                      onChange={(e) => onPasswordInputChange(e)} style={{ fontWeight: '200' }} />
                  </Col>
                </Form.Group>

                <hr />

                <Form.Group as={Row} className="mb-1 mt-4">
                  <Form.Label column sm="8" style={{ color: 'rgba(200,0,0)' }}>
                    {errorMessage}
                  </Form.Label>
                  <Col sm="2">
                    <Button variant="outline-secondary" size="sm" type="submit" className="mb-3" style={{fontWeight: '300' }}>
                      {t("I18N_Submit")}
                    </Button>
                  </Col>
                  <Col sm="2">
                    <Button onClick={handleClose} variant="outline-secondary" size="sm" type="close" className="mb-3" style={{ fontWeight: '300' }}>
                      {t("I18N_Cancel")}
                    </Button>
                  </Col>
                </Form.Group>

              </Form>
            </Container>
          </Modal.Body>
        </fieldset>
        <LoadingSpinnerForModal loading={loading}/>
      </Modal>
    </React.Fragment>
  )
}

export default NewPwdSecQnA;
